export const DEVELOPMENT = "development";
export const QA = "qa";
export const PRODUCTION = "production";

export default class Config {
  static buildEnvironment() {
    return process.env.VUE_APP_BUILD_ENVIRONMENT;
  }

  isProdBuild() {
    return Config.buildEnvironment() === PRODUCTION;
  }

  isQABuild() {
    return Config.buildEnvironment() === QA;
  }

  static isProdAllowed() {
    return Config.buildEnvironment() === PRODUCTION;
  }

  static isQAAllowed() {
    return Config.buildEnvironment() === QA || Config.buildEnvironment() === PRODUCTION;
  }

  static isDevAllowed() {
    return true;
  }

  static allowedEnvironments() {
    let envs = [DEVELOPMENT];
    if (this.isQAAllowed()) envs.push(QA);
    if (this.isProdAllowed()) envs.push(PRODUCTION);
    return envs;
  }

  static isDevServer() {
    return process.env.VUE_APP_IS_DEV_SERVER === "true";
  }

  constructor(environment) {
    function throwForbiddenEnvError() {
      const error = `Forbidden environment ${environment}`;
      const cause = `This build was targeted for ${Config.buildEnvironment()}`;
      const moreInfo = `the following envs are allowed: ${Config.allowedEnvironments()}`;
      throw new Error(`${error}. ${cause}, ${moreInfo}`);
    }
    if (environment === PRODUCTION) {
      if (!Config.isProdAllowed()) throwForbiddenEnvError();
      this.environment = environment;
    } else if (environment === QA) {
      if (!Config.isQAAllowed()) throwForbiddenEnvError();
      this.environment = environment;
    } else if (environment === DEVELOPMENT) {
      this.environment = environment;
    } else {
      console.warn(`Unkown environment ${environment}, using default: ${DEVELOPMENT}`);
      this.environment = DEVELOPMENT;
    }
  }

  isProd() {
    return this.environment === PRODUCTION;
  }

  isQA() {
    return this.environment === QA;
  }

  isDev() {
    return !this.isProd() && !this.isQA();
  }

  kongUrl(company) {
    const PFG = "principal";
    if (this.isProd()) {
      if (company === PFG) return process.env.VUE_APP_OAUTH_GET_TOKEN_API_PRINCIPAL_URL_PROD;
      else return process.env.VUE_APP_OAUTH_GET_TOKEN_API_CUPRUM_URL_PROD;
    } else if (this.isQA()) {
      if (company === PFG) return process.env.VUE_APP_OAUTH_GET_TOKEN_API_PRINCIPAL_URL_QA;
      else return process.env.VUE_APP_OAUTH_GET_TOKEN_API_CUPRUM_URL_QA;
    } else {
      if (company === PFG) return process.env.VUE_APP_OAUTH_GET_TOKEN_API_PRINCIPAL_URL_DEV;
      else return process.env.VUE_APP_OAUTH_GET_TOKEN_API_CUPRUM_URL_DEV;
    }
  }

  kafkaRestUrl(kongModeJWT) {
    if (this.isProd()) {
      if (kongModeJWT) return process.env.VUE_APP_FULFILLMENT_TRACE_API_PROD_JWT;
      else return process.env.VUE_APP_FULFILLMENT_TRACE_API_PROD;
    } else if (this.isQA()) {
      if (kongModeJWT) return process.env.VUE_APP_FULFILLMENT_TRACE_API_QA_JWT;
      else return process.env.VUE_APP_FULFILLMENT_TRACE_API_QA;
    } else {
      if (kongModeJWT) return process.env.VUE_APP_FULFILLMENT_TRACE_API_DEV_JWT;
      else return process.env.VUE_APP_FULFILLMENT_TRACE_API_DEV;
    }
  }

  authApiUrl(kongModeJWT, apiKeyMode) {
    if (this.isProd()) {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_PROD_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_PROD_AK;
      else return process.env.VUE_APP_AUTH_API_PROD;
    }
    if (this.isQA()) {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_QA_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_QA_AK;
      else return process.env.VUE_APP_AUTH_API_QA;
    } else {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_DEV_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_DEV_AK;
      else return process.env.VUE_APP_AUTH_API_DEV;
    }
  }

  authApiUrlPFG(kongModeJWT, apiKeyMode) {
    if (this.isProd()) {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_PFG_PROD_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_PFG_PROD_AK;
      else return process.env.VUE_APP_AUTH_API_PFG_PROD;
    }
    if (this.isQA()) {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_PFG_QA_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_PFG_QA_AK;
      else return process.env.VUE_APP_AUTH_API_PFG_QA;
    } else {
      if (kongModeJWT) return process.env.VUE_APP_AUTH_API_PFG_DEV_JWT;
      else if (apiKeyMode) return process.env.VUE_APP_AUTH_API_PFG_DEV_AK;
      else return process.env.VUE_APP_AUTH_API_PFG_DEV;
    }
  }

  portalApiKey() {
    return process.env.VUE_APP_FULFILLMENT_PORTAL_API_KEY;
  }

  wrapperUrl(company) {
    let url = process.env.VUE_APP_FACIAL_RECOGNITION_WRAPPER_URL_DEV;
    if (this.isQABuild()) url = process.env.VUE_APP_FACIAL_RECOGNITION_WRAPPER_URL_QA;
    if (this.isProdBuild()) url = process.env.VUE_APP_FACIAL_RECOGNITION_WRAPPER_URL_PROD;
    const companies = ["cuprum", "principal"];
    const validCompany = companies.includes(company.toLowerCase()) ? company.toLowerCase() : companies[0];
    let version = process.env.VUE_APP_VERSION;
    const rcIndex = version.indexOf("-rc");
    if (rcIndex > 0) {
      version = version.substring(0, rcIndex + 3);
    }
    return url.replace("{{VERSION}}", version).replace("{{COMPANY}}", validCompany);
  }

  tocAutocaptureUrl() {
    if (this.isProd()) return process.env.VUE_APP_TOC_AUTOCAPTURE_URL_PROD;
    if (this.isQA()) return process.env.VUE_APP_TOC_AUTOCAPTURE_URL_QA;
    return process.env.VUE_APP_TOC_AUTOCAPTURE_URL_DEV;
  }

  tocLivenessUrl() {
    if (this.isProd()) return process.env.VUE_APP_TOC_LIVENESS_URL_PROD;
    if (this.isQA()) return process.env.VUE_APP_TOC_LIVENESS_URL_QA;
    return process.env.VUE_APP_TOC_LIVENESS_URL_DEV;
  }

  successImage() {
    if (this.isProd()) return process.env.VUE_APP_FULFILLMENT_SUCCESS_IMAGE_PROD;
    else return process.env.VUE_APP_FULFILLMENT_SUCCESS_IMAGE_QA;
  }

  errorImage() {
    if (this.isProd()) return process.env.VUE_APP_FULFILLMENT_ERROR_IMAGE_PROD;
    else return process.env.VUE_APP_FULFILLMENT_ERROR_IMAGE_QA;
  }

  printInfo() {
    console.log(
      `%c fulfillment-auth-widget %c ${process.env.VUE_APP_VERSION} %c`,
      "background:#294594 ; padding: 1px; border-radius: 3px 0 0 3px;  color: #fff",
      "background:#4faeff ; padding: 1px; border-radius: 0 3px 3px 0;  color: #fff",
      "background:transparent"
    ); // eslint-disable-line no-console
    console.log(`BUILD ENVIRONMENT: ${Config.buildEnvironment()}`); // eslint-disable-line no-console
    console.log(`CONFIGURED ENVIRONMENT: ${this.environment}`); // eslint-disable-line no-console
    console.log(`AUTH_API: ${this.authApiUrl(false)}`); // eslint-disable-line no-console
    console.log(`AUTH_API_JWT: ${this.authApiUrl(true)}`); // eslint-disable-line no-console
    console.log(`KONG_API: ${this.kongUrl()}`); // eslint-disable-line no-console
    console.log(`TRACE_API: ${this.kafkaRestUrl(false)}`); // eslint-disable-line no-console
    console.log(`TRACE_API_JWT: ${this.kafkaRestUrl(true)}`); // eslint-disable-line no-console
  }
}
